export class ApiError extends Error {
  constructor(message, status = 500, code = 'API_ERROR') {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export async function api(path, options = {}) {
  const response = await fetch(path, {
    credentials: 'same-origin',
    headers: { 'accept': 'application/json', ...(options.headers || {}) },
    ...options,
  });
  let body = null;
  try { body = await response.json(); } catch { body = null; }
  if (!response.ok) {
    throw new ApiError(body?.message || `Request failed (${response.status})`, response.status, body?.code || 'API_ERROR');
  }
  return body;
}

export const UK_LOCALE = 'en-GB';
export const UK_TIME_ZONE = 'Europe/London';

export const money = (minor, currency = 'GBP') => {
  const amount = Number(minor || 0) / 100;
  try { return new Intl.NumberFormat(UK_LOCALE, { style: 'currency', currency }).format(amount); }
  catch { return `£${amount.toFixed(2)}`; }
};

export const emailAddress = value => {
  const email = String(value ?? '').trim();
  return email ? email.toLowerCase() : '—';
};

const UK_WORDS = {
  organization: 'organisation',
  organizations: 'organisations',
  authorized: 'authorised',
  unauthorized: 'unauthorised',
  canceled: 'cancelled',
  canceling: 'cancelling',
};

export const britishEnglish = value => String(value ?? '').replace(
  /\b(organization|organizations|authorized|unauthorized|canceled|canceling)\b/gi,
  match => {
    const replacement = UK_WORDS[match.toLowerCase()] || match;
    if (match === match.toUpperCase()) return replacement.toUpperCase();
    if (match[0] === match[0].toUpperCase()) return replacement[0].toUpperCase() + replacement.slice(1);
    return replacement;
  },
);

function validDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export const dateTime = value => {
  if (!value) return '—';
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return dateOnly(raw);
  const date = validDate(value);
  if (!date) return '—';
  return new Intl.DateTimeFormat(UK_LOCALE, {
    timeZone: UK_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).format(date);
};

export const dateOnly = value => {
  if (!value) return '—';
  const raw = String(value).trim();
  const dateOnlyMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return `${day}/${month}/${year}`;
  }
  const date = validDate(value);
  if (!date) return '—';
  return new Intl.DateTimeFormat(UK_LOCALE, {
    timeZone: UK_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
};
